<template>
  <transition
    name="custom-classes-transition"
    enter-active-class="animated fadeIn faster"
    leave-active-class="animated fadeOut faster"
  >
    <div id="playlist-select-modal" class="modal is-active" v-if="isVisible">
      <div class="modal-background" @click="close"></div>

      <div class="modal-content card">
        <div class="card-content">
          <h2 class="title">Add {{ selectedAudioFileIds.length }} Audio File(s) to Playlist</h2>

          <!-- Search Input -->

          <div class="m-b-20">
                          <div class="control has-icons-right">
                <input
                  class="input is-medium"
                  type="search"
                  placeholder="Search Playlists"
                  v-model="searchQuery"
                  :disabled="isLoading"
                  @input="handleSearchInput"
                >
                <span class="icon is-right is-medium">
                  <IconSearch v-if="searchQuery === ''" />
                  <button
                    v-if="searchQuery !== ''"
                    @click="clearSearch"
                    class="delete is-medium"
                    :disabled="isLoading"
                  ></button>
                </span>
              </div>
          </div>

          <!-- Loading indicator -->
          <progress
            v-if="isLoading"
            class="progress is-small is-primary"
            max="100"
          ></progress>

          <!-- Playlist List -->
          <div class="playlist-list-container" style="max-height: 300px; overflow-y: auto;">
            <div v-if="!isLoading && playlists.length === 0" class="has-text-centered p-20">
              <p class="has-text-grey">No playlists found</p>
            </div>
            <ul v-else class="menu-list" style="list-style: none;">
              <li v-for="playlist in playlists" :key="playlist.id">
                <a
                  class="playlist-item"
                  :class="{ 'is-active': selectedPlaylist && selectedPlaylist.id === playlist.id }"
                  @click="selectPlaylist(playlist)"
                  :disabled="isLoading"
                >
                  <div class="playlist-info">
                    <div class="playlist-title">{{ playlist.creation_date | momentDate }} - {{ playlist.title }}</div>
                    <!-- <div class="playlist-date">{{ playlist.creation_date | momentDate }}</div> -->
                  </div>
                </a>
              </li>
            </ul>
          </div>

          <!-- Action Buttons -->
          <div class="level is-mobile m-t-30">
            <div class="level-left">
              <div class="level-item">
                <button
                  type="button"
                  class="button is-primary"
                  :disabled="!selectedPlaylist || isLoading"
                  @click="addToPlaylist"
                >
                  {{ isLoading ? 'Adding...' : 'Add to Playlist' }}
                </button>
              </div>
              <div class="level-item">
                <button
                  type="button"
                  class="button is-primary is-inverted"
                  @click="close"
                  :disabled="isLoading"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        <button class="modal-close is-large" @click="close" :disabled="isLoading"></button>
      </div>
    </div>
  </transition>
</template>

<script>
import PlaylistsApi from '@/services/api/Playlists'
import { IconSearch } from '@/Icons/IconBase'

export default {
  name: 'PlaylistSelectModal',

  components: {
    IconSearch
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    selectedSongs: {
      type: Array,
      default: () => []
    },
    selectedAudioFileIds: {
      type: Array,
      default: () => []
    },
    selectedCount: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      searchQuery: '',
      playlists: [],
      selectedPlaylist: null,
      isLoading: false,
      searchTimeout: null,
      lastSearchQuery: null
    }
  },

  watch: {
    isVisible(newValue) {
      if (newValue) {
        this.searchQuery = ''
        this.selectedPlaylist = null
        this.lastSearchQuery = null
        this.loadPlaylists()
      }
    }
  },

  methods: {
    close() {
      if (!this.isLoading) {
        this.$emit('close')
      }
    },

    selectPlaylist(playlist) {
      if (!this.isLoading) {
        this.selectedPlaylist = playlist
      }
    },

    handleSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Set new timeout for 500ms
      this.searchTimeout = setTimeout(() => {
        // Only search if the query has actually changed
        if (this.searchQuery !== this.lastSearchQuery) {
          this.lastSearchQuery = this.searchQuery
          this.loadPlaylists(this.searchQuery)
        }
      }, 500)
    },

    clearSearch() {
      this.searchQuery = ''
      this.lastSearchQuery = ''
      this.loadPlaylists('')
    },

    loadPlaylists(query = null) {
      this.isLoading = true

      PlaylistsApi.searchPlaylists(this.$store.getters.getActiveAccountId, 1, 50, query)
        .then(resp => {
          this.playlists = resp.data.data
          this.isLoading = false
        })
        .catch(err => {
          console.error('Error loading playlists:', err)
          this.$store.commit('error', 'Could not load playlists.')
          this.isLoading = false
        })
    },

    async addToPlaylist() {
      if (!this.selectedPlaylist || this.isLoading) {
        return
      }

      this.isLoading = true

      try {
        // Use the bulk addAudioFiles method
        await PlaylistsApi.addAudioFiles(this.selectedPlaylist.id, this.selectedAudioFileIds)

        this.$store.commit('alert', `${this.selectedAudioFileIds.length} audio file(s) added to "${this.selectedPlaylist.title}".`)
        this.close()
        this.$emit('playlist-added')
      } catch (err) {
        console.error('Error adding to playlist:', err)
        if (err.response) {
          this.$store.commit('error', err.response.data.message || 'Failed to add audio files to playlist.')
        } else {
          this.$store.commit('error', 'Failed to add audio files to playlist. Please try again.')
        }
      } finally {
        this.isLoading = false
      }
    }
  },

  beforeDestroy() {
    // Clean up timeout when component is destroyed
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }
}
</script>

<style scoped>
.playlist-item {
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.playlist-item:hover {
  background-color: #f5f5f5;
  color: hsl(198, 82%, 64%);
}

.playlist-item.is-active {
  background-color: hsl(198, 82%, 64%);
  color: white;
}

.playlist-info {
  flex: 1;
}

.playlist-title {
  font-weight: 500;
}

.playlist-date {
  font-size: 0.875rem;
  opacity: 0.7;
}

.playlist-list-container {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background-color: #fafafa;
}
</style>
